import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getSupabaseClient, isSupabaseConfigured } from '../services/supabaseClient';
import { SEO } from '../components/SEO';
import './ConfirmEmailPage.css';

export function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const confirmEmail = async () => {
      const token = searchParams.get('token');
      const email = searchParams.get('email');

      if (!token || !email) {
        setStatus('error');
        setMessage('Invalid confirmation link. Please check your email and try again.');
        return;
      }

      try {
        const supabase = getSupabaseClient();
        
        if (!supabase || !isSupabaseConfigured()) {
          setStatus('error');
          setMessage('Service temporarily unavailable. Please try again later.');
          return;
        }

        // Find the email record with this token
        const { data, error: fetchError } = await supabase
          .from('deals_email_list')
          .select('id, email, confirmed_at, created_at')
          .eq('email', email.toLowerCase().trim())
          .eq('confirmation_token', token)
          .single();

        if (fetchError || !data) {
          setStatus('error');
          setMessage('Invalid or expired confirmation link. Please request a new confirmation email.');
          return;
        }

        // Check if already confirmed
        if (data.confirmed_at) {
          setStatus('success');
          setMessage('Your email is already confirmed! You\'re all set to receive exclusive deals.');
          return;
        }

        // Check if token is expired (7 days)
        const createdAt = new Date(data.created_at);
        const now = new Date();
        const daysDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);

        if (daysDiff > 7) {
          setStatus('error');
          setMessage('This confirmation link has expired. Please sign up again to receive a new confirmation email.');
          return;
        }

        // Confirm the email
        const { error: updateError } = await supabase
          .from('deals_email_list')
          .update({
            confirmed_at: new Date().toISOString(),
          })
          .eq('id', data.id)
          .eq('confirmation_token', token);

        if (updateError) {
          throw updateError;
        }

        setStatus('success');
        setMessage('Email confirmed successfully! You\'ll now receive exclusive peptide deals and price alerts.');
      } catch (error) {
        console.error('Error confirming email:', error);
        setStatus('error');
        setMessage('Something went wrong. Please try again later or contact support.');
      }
    };

    confirmEmail();
  }, [searchParams]);

  return (
    <>
      <SEO
        title="Confirm Your Email - Peptide Finder"
        description="Confirm your email address to start receiving exclusive peptide deals and price alerts."
      />
      <div className="confirm-email-page">
        <div className="confirm-email-container">
          {status === 'loading' && (
            <div className="confirm-email-content">
              <div className="confirm-email-spinner">⏳</div>
              <h1>Confirming your email...</h1>
              <p>Please wait while we verify your email address.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="confirm-email-content confirm-email-success">
              <div className="confirm-email-icon">✓</div>
              <h1>Email Confirmed!</h1>
              <p>{message}</p>
              <button
                onClick={() => navigate('/exclusive-deals')}
                className="confirm-email-button"
              >
                Back to Exclusive Deals
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="confirm-email-content confirm-email-error">
              <div className="confirm-email-icon">✗</div>
              <h1>Confirmation Failed</h1>
              <p>{message}</p>
              <button
                onClick={() => navigate('/exclusive-deals')}
                className="confirm-email-button"
              >
                Back to Exclusive Deals
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
